import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { AuthDialog } from "@/components/AuthDialog";
import { api } from "@/lib/api";
import { nativeShare } from "@/lib/share";
import { useAuth } from "@/context/AuthContext";
import { titleCaseName } from "@/lib/utils";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Share2, Images, MessageSquare, Users, ImagePlus, Send, X, Pencil, Trash2, Bell, BellRing, Link as LinkIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ringStyle } from "@/lib/flagRing";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const AVATAR_COLORS = ["bg-rose-500/80", "bg-amber-500/80", "bg-emerald-500/80", "bg-sky-500/80", "bg-violet-500/80", "bg-fuchsia-500/80", "bg-teal-500/80"];
const avatarColor = (name) => AVATAR_COLORS[[...(name || "?")].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
const avatarInitial = (name) => (name || "?").trim().charAt(0).toUpperCase() || "?";
const timeAgo = (dateStr, lang) => {
  try { return new Date(dateStr).toLocaleDateString(lang || "fr", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return ""; }
};

const resizeImage = (file, max = 1200, quality = 0.82) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    const img = new Image();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      let { width, height } = img;
      if (width > max || height > max) {
        if (width >= height) { height = Math.round((height * max) / width); width = max; }
        else { width = Math.round((width * max) / height); height = max; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    reader.readAsDataURL(file);
  });

export default function GroupMemories({ shared = false }) {
  const { id, token } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(-1);

  // contribution form (shared view)
  const [cName, setCName] = useState("");
  const [cMsg, setCMsg] = useState("");
  const [cPhotos, setCPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const photoRef = useRef();

  // moderation (owner)
  const [editItem, setEditItem] = useState(null);
  const [editMsg, setEditMsg] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // follow / subscribe (shared view)
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const pendingFollowRef = useRef(false);
  const pendingPhotoRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const url = shared ? `/groups/shared/${token}` : `/me/groups/${id}/memories`;
      const res = await api.get(url);
      setData(res.data);
      setFollowing(!!res.data?.group?.is_following);
      setNotFound(false);
    } catch (e) {
      setNotFound(true);
    } finally {
      setFetching(false);
    }
  }, [shared, id, token]);

  useEffect(() => { load(); }, [load]);

  const shareUrl = data?.group?.share_token ? `${BACKEND}/api/share/famille/${data.group.share_token}` : "";
  const shareNames = (data?.group?.candles || []).map((c) => c.deceased_name).filter(Boolean).slice(0, 6).join(", ");
  const shareMsg = `🕯️ ${data?.group?.name || t("family.title")}${shareNames ? ` · ${shareNames}` : ""} — ${t("family.title")}`;

  const doShare = async () => {
    if (!shareUrl) return;
    const res = await nativeShare({ title: data?.group?.name, text: shareMsg, url: shareUrl });
    if (res === "copied") toast.success(t("family.linkCopied"));
  };
  const copyLink = async () => {
    if (!shareUrl) return;
    try { await navigator.clipboard.writeText(shareUrl); toast.success(t("family.linkCopied")); }
    catch { toast.error(t("memorial.error")); }
  };

  const doFollow = async () => {
    if (!user) { pendingFollowRef.current = true; setAuthOpen(true); return; }
    setFollowBusy(true);
    try {
      if (following) {
        await api.delete(`/groups/shared/${token}/follow`);
        setFollowing(false); toast.success(t("family.unfollowed"));
      } else {
        await api.post(`/groups/shared/${token}/follow`);
        setFollowing(true); toast.success(t("family.followed"));
      }
    } catch (e) { toast.error(e.response?.data?.detail || t("memorial.error")); }
    finally { setFollowBusy(false); }
  };

  const onAuthSuccess = async () => {
    setAuthOpen(false);
    if (pendingFollowRef.current) {
      pendingFollowRef.current = false;
      try { await api.post(`/groups/shared/${token}/follow`); setFollowing(true); toast.success(t("family.followed")); }
      catch { /* ignore */ }
    } else {
      toast.success(t("family.connected"));
    }
    load();
    if (pendingPhotoRef.current) {
      pendingPhotoRef.current = false;
      setTimeout(() => photoRef.current?.click(), 300);
    }
  };

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const results = [];
    for (const f of files.slice(0, 10 - cPhotos.length)) {
      try { results.push(await resizeImage(f)); } catch { /* skip */ }
    }
    setCPhotos((arr) => [...arr, ...results].slice(0, 10));
  };
  const clickAddPhoto = () => {
    if (!user) { pendingPhotoRef.current = true; setAuthOpen(true); return; }
    photoRef.current?.click();
  };

  const submitContribution = async (e) => {
    e.preventDefault();
    if (!cMsg.trim() && cPhotos.length === 0) return;
    setSending(true);
    try {
      await api.post(`/groups/shared/${token}/contribute`, { author_name: cName, message: cMsg, photos: cPhotos });
      toast.success(t("family.publishedNotice"));
      setCName(""); setCMsg(""); setCPhotos([]);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("memorial.error"));
    } finally { setSending(false); }
  };

  // owner moderation actions (scope-aware: family contribution vs direct candle message)
  const remove = async (m) => {
    const url = m.scope === "family"
      ? `/me/groups/${id}/contributions/${m.id}`
      : `/me/candle-messages/${m.id}`;
    try { await api.delete(url); toast.success(t("family.deleted")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("memorial.error")); }
  };
  const removePhoto = async (m, idx) => {
    const url = m.scope === "family"
      ? `/me/groups/${id}/contributions/${m.id}/photo/${idx}`
      : `/me/candle-messages/${m.id}/photo/${idx}`;
    try { await api.delete(url); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("memorial.error")); }
  };
  const openEdit = (item) => { setEditItem(item); setEditMsg(item.message || ""); };
  const saveEdit = async () => {
    setSavingEdit(true);
    const url = editItem.scope === "family"
      ? `/me/groups/${id}/contributions/${editItem.id}`
      : `/me/candle-messages/${editItem.id}`;
    try {
      await api.patch(url, { message: editMsg });
      toast.success(t("family.saved"));
      setEditItem(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("memorial.error")); }
    finally { setSavingEdit(false); }
  };

  if (fetching) {
    return <div className="min-h-[100dvh]"><Header /><div className="flex justify-center py-20"><Loader2 className="animate-spin text-sky-400" /></div></div>;
  }
  if (notFound || !data) {
    return <div className="min-h-[100dvh]"><Header /><div className="mx-auto max-w-2xl px-5 py-20 text-center text-muted-foreground">{t("memorial.notFound")}</div></div>;
  }

  const { group, messages, photos } = data;
  const photoUrls = photos.map((p) => `${BACKEND}${p.url}`);

  const photoSrc = (u) => `${BACKEND}${u}`;

  return (
    <div className="min-h-[100dvh]">
      <Header />
      <div className="mx-auto max-w-3xl px-5 py-8 pb-28">
        {!shared && (
          <button onClick={() => navigate("/souvenirs")} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="back-to-groups-btn">
            <ArrowLeft size={15} /> {t("family.backToGroups")}
          </button>
        )}

        <div className="mb-2 flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {group.cover_url && (
              <img src={`${BACKEND}${group.cover_url}`} alt={group.name} className="h-14 w-14 shrink-0 rounded-xl border border-amber-300/50 object-cover" data-testid="group-detail-cover" />
            )}
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.18em] text-amber-300/80">{shared ? t("family.sharedTitle") : t("family.title")}</p>
              <h1 className="flex items-center gap-2 text-2xl font-medium"><Users size={20} className="text-amber-300" /> {group.name}</h1>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {!shared && (
              <>
                <Button size="sm" variant="outline" className="rounded-full border-white/15 px-2.5" onClick={copyLink} title={t("memorial.copyLink")} data-testid="copy-link-btn">
                  <LinkIcon size={15} />
                </Button>
                <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={doShare} data-testid="detail-share-btn">
                  <Share2 size={14} className="mr-1" /> {t("family.share")}
                </Button>
              </>
            )}
            {shared && (
              <Button size="sm" onClick={doFollow} disabled={followBusy} className={`rounded-full font-semibold ${following ? "border border-emerald-400/50 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/20" : "bg-amber-300 text-black hover:bg-amber-200"}`} data-testid="follow-group-btn">
                {followBusy ? <Loader2 className="animate-spin" size={14} /> : following ? <><BellRing size={14} className="mr-1.5" /> {t("family.following")}</> : <><Bell size={14} className="mr-1.5" /> {t("family.follow")}</>}
              </Button>
            )}
          </div>
        </div>

        {/* Espace partagé : partage SMS + Partager sous le bouton Abonné(e) */}
        {shared && (
          <div className="mb-4 flex gap-2" data-testid="shared-share-row">
            <Button size="sm" variant="outline" className="flex-1 rounded-full border-white/15" onClick={copyLink} data-testid="copy-link-btn-shared">
              <LinkIcon size={15} className="mr-1.5" /> {t("memorial.copyLink")}
            </Button>
            <Button size="sm" variant="outline" className="flex-1 rounded-full border-white/15" onClick={doShare} data-testid="detail-share-btn">
              <Share2 size={14} className="mr-1.5" /> {t("family.share")}
            </Button>
          </div>
        )}

        {/* candle chips */}
        <div className="mb-6 mt-3 flex flex-wrap gap-2">
          {group.candles.map((c) => (
            <div key={c.id} className="flex items-center gap-2 rounded-full border border-amber-300/20 bg-amber-300/[0.04] py-1 pl-1 pr-3" data-testid="group-candle-chip">
              <div className={`rounded-full ${ringStyle(c.flag_colors) ? "p-[2px]" : ""}`} style={ringStyle(c.flag_colors) || undefined} title={c.country_name || ""}>
                <img src={`${BACKEND}${c.photo_url}`} alt={c.deceased_name} className={`h-7 w-7 rounded-full object-cover ${ringStyle(c.flag_colors) ? "" : "border border-amber-300/60"}`} />
              </div>
              <span className="text-sm text-amber-100/90">{c.deceased_name}</span>
            </div>
          ))}
        </div>

        {/* Contribution form (shared view) */}
        {shared && (
          <>
          {!following && (
            <button onClick={doFollow} className="mb-4 flex w-full items-center gap-2 rounded-2xl border border-sky-400/30 bg-sky-400/[0.06] px-4 py-3 text-left transition-colors hover:bg-sky-400/[0.12]" data-testid="follow-hint">
              <Bell size={16} className="shrink-0 text-sky-300" />
              <span className="text-sm text-sky-100/90">{t("family.followHint")}</span>
            </button>
          )}
          <div className="mb-6 rounded-3xl border border-amber-300/25 bg-amber-300/[0.05] p-5 shadow-[0_0_60px_-25px_rgba(252,211,77,0.5)]" data-testid="contribute-block">
            <div className="mb-1 flex items-center gap-2 text-amber-300">
              <Send size={15} />
              <h3 className="text-sm font-semibold">{t("family.contributeTitle")}</h3>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">{t("family.contributeSub")}</p>
            <form onSubmit={submitContribution} className="space-y-2" data-testid="contribute-form">
              <Input placeholder={t("family.yourName")} value={cName} onChange={(e) => setCName(titleCaseName(e.target.value))} data-testid="contribute-name" />
              <Textarea placeholder={t("family.yourMessage")} rows={4} className="min-h-[110px] resize-y text-base" value={cMsg} onChange={(e) => setCMsg(e.target.value)} data-testid="contribute-message" />
              <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} />
              {cPhotos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {cPhotos.map((p, i) => (
                    <div key={i} className="relative">
                      <img src={p} alt="" className="h-16 w-16 rounded-lg object-cover" />
                      <button type="button" onClick={() => setCPhotos((arr) => arr.filter((_, j) => j !== i))} className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5" data-testid={`contribute-photo-remove-${i}`}><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
              <Button type="button" variant="outline" size="sm" onClick={clickAddPhoto} disabled={cPhotos.length >= 10} className="w-full rounded-full border-white/15" data-testid="contribute-add-photo">
                <ImagePlus size={15} className="mr-1.5" /> {user ? t("create.addPhoto") : t("family.connectForPhotos")} {cPhotos.length > 0 ? `(${cPhotos.length}/10)` : (user ? `· ${t("family.photosMax")}` : "")}
              </Button>
              <Button type="submit" disabled={sending} className="w-full rounded-full bg-amber-300 font-semibold text-black hover:bg-amber-200" data-testid="contribute-submit">
                {sending ? <Loader2 className="animate-spin" size={16} /> : <><Send size={15} className="mr-2" /> {t("family.send")}</>}
              </Button>
              <p className="text-center text-[11px] text-muted-foreground">{t("family.moderationNote")}</p>
            </form>
          </div>
          </>
        )}

        {/* Pending moderation removed: contributions are published immediately */}

        <Tabs defaultValue="photos" className="w-full">
          <TabsList className="grid w-full grid-cols-2 rounded-full bg-card/50 p-1" data-testid="memories-tabs">
            <TabsTrigger value="photos" className="rounded-full data-[state=active]:bg-amber-300 data-[state=active]:text-black" data-testid="tab-photos">
              <Images size={15} className="mr-1.5" /> {t("family.photos")} ({photos.length})
            </TabsTrigger>
            <TabsTrigger value="messages" className="rounded-full data-[state=active]:bg-amber-300 data-[state=active]:text-black" data-testid="tab-messages">
              <MessageSquare size={15} className="mr-1.5" /> {t("family.messages")} ({messages.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="photos" className="mt-5">
            {photos.length === 0 ? (
              <p className="py-14 text-center text-sm text-muted-foreground" data-testid="photos-empty">{t("family.noPhotos")}</p>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="photos-grid">
                {photos.map((p, i) => (
                  <button key={i} type="button" onClick={() => setLightbox(i)} className="group relative aspect-square overflow-hidden rounded-xl border border-white/10" data-testid={`photo-cell-${i}`}>
                    <img src={photoSrc(p.url)} alt={p.deceased_name} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                    <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/80 to-transparent px-2 py-1 text-[10px] text-white/90">{p.deceased_name}</span>
                  </button>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="messages" className="mt-5">
            {messages.length === 0 ? (
              <p className="py-14 text-center text-sm text-muted-foreground" data-testid="messages-empty">{t("family.noMessages")}</p>
            ) : (
              <div className="space-y-3" data-testid="messages-list">
                {messages.map((m) => (
                  <div key={m.id} className="rounded-2xl border border-white/10 bg-card/30 p-4 fade-in" data-testid="memory-message">
                    <div className="flex items-center gap-3">
                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${avatarColor(m.author_name)}`}>{avatarInitial(m.author_name)}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2">
                          <span className="font-medium">{m.author_name}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] ${m.scope === "family" ? "bg-sky-400/15 text-sky-200" : "bg-amber-300/10 text-amber-200/90"}`}>
                            {m.scope === "family" ? t("family.familyLabel") : `${t("family.for")} ${m.deceased_name}`}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground">{timeAgo(m.created_at, i18n.language)}</span>
                      </div>
                      {!shared && (
                        <div className="flex shrink-0 gap-1">
                          <button onClick={() => openEdit(m)} className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground" title={t("family.edit")} data-testid="message-edit"><Pencil size={15} /></button>
                          <button onClick={() => remove(m)} className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-destructive/15 hover:text-destructive" title={t("family.delete")} data-testid="message-delete"><Trash2 size={15} /></button>
                        </div>
                      )}
                    </div>
                    {m.message && <p className="mt-2 whitespace-pre-line text-sm text-foreground/90">{m.message}</p>}
                    {m.photo_urls?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {m.photo_urls.map((u, i) => {
                          const globalIdx = photoUrls.indexOf(photoSrc(u));
                          return (
                            <div key={i} className="relative">
                              <img src={photoSrc(u)} alt="" onClick={() => globalIdx >= 0 && setLightbox(globalIdx)} className="h-16 w-16 cursor-pointer rounded-lg border border-white/10 object-cover" data-testid="memory-message-photo" />
                              {!shared && (
                                <button type="button" onClick={() => removePhoto(m, i)} className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5" title={t("family.deletePhoto")} data-testid={`message-photo-remove-${i}`}><X size={12} /></button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {lightbox >= 0 && (
        <PhotoLightbox photos={photoUrls} index={lightbox} onClose={() => setLightbox(-1)} onIndex={setLightbox} />
      )}

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialMode="register" onSuccess={onAuthSuccess} />

      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent className="glass border-white/10 sm:max-w-md" data-testid="edit-message-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">{t("family.editMessage")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <Textarea rows={5} value={editMsg} onChange={(e) => setEditMsg(e.target.value)} className="min-h-[130px] resize-y text-base" data-testid="edit-message-textarea" />
            <Button onClick={saveEdit} disabled={savingEdit} className="w-full rounded-full" data-testid="edit-message-save">
              {savingEdit ? <Loader2 className="animate-spin" size={16} /> : t("family.save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
